import { BrandLogo } from '@seek/branding-assets';

import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonLink,
  Column,
  Columns,
  Heading,
  IconEdit,
  Inline,
  Rating,
  Text,
  TextLink,
  TextLinkButton,
} from 'braid-design-system';
import {
  buildWriteAReviewURI,
  useConfig,
  type CompanyProfile,
} from '../../../';

import translations from './.vocab';

export type CompanyBrandingSectionModel = Pick<
  CompanyProfile,
  'branding' | 'name' | 'rating' | 'id' | 'zone'
>;

interface Props {
  aboutPageLink: string;
  navigateToAboutTab: () => void;
  navigateToReviewsTab: () => void;
  trackWriteAReviewClicked: () => void;
  model?: Omit<CompanyBrandingSectionModel, 'id'>;
}

const createImageObject = (propertyName: string, imageUrl?: string) => ({
  url: imageUrl || '',
  strategies: {
    [propertyName]: imageUrl || '',
  },
  id: imageUrl || '',
});

export function CompanyProfileSummary({
  model,
  navigateToReviewsTab,
  navigateToAboutTab,
  trackWriteAReviewClicked,
  aboutPageLink,
}: Props) {
  const { t } = useTranslations(translations);
  const { country, language, environment, consolidated } = useConfig();
  const logoImageAsset = createImageObject(
    'jdpLogo',
    model?.branding?.logoImageUrl,
  );

  const handleWriteAReviewClicked = async () => {
    trackWriteAReviewClicked();
    await new Promise((resolve) => setTimeout(resolve, 500));
  };
  const totalRating = model?.rating?.average || 0;
  const numberOfReviews = model?.rating?.count;

  return (
    <Columns collapseBelow="tablet" space="large" align="left" alignY="center">
      <Column>
        <Box
          display={{ mobile: 'inlineBlock', tablet: 'flex' }}
          flexDirection={{ mobile: 'column', tablet: 'row' }}
        >
          <Box
            paddingBottom={{
              mobile: 'medium',
              tablet: 'none',
            }}
            paddingRight={{
              mobile: 'none',
              tablet: 'medium',
            }}
          >
            <BrandLogo logo={logoImageAsset} />
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'row', tablet: 'column' }}
            alignItems={{ mobile: 'center', tablet: 'flexStart' }}
            justifyContent="center"
          >
            {model?.name ? (
              <Box
                paddingBottom={{
                  mobile: 'none',
                  tablet: 'medium',
                }}
                paddingRight={{
                  mobile: 'small',
                  tablet: 'none',
                }}
              >
                <TextLink
                  href={aboutPageLink}
                  onClick={(e) => {
                    e.preventDefault();
                    navigateToAboutTab();
                  }}
                >
                  <Heading level="4" component="span">
                    {model.name}
                  </Heading>
                </TextLink>
              </Box>
            ) : null}
            {model?.rating ? (
              <Inline space="xxsmall" alignY="center">
                <Rating rating={totalRating} variant="minimal" />
                <Text>
                  <TextLinkButton onClick={navigateToReviewsTab} weight="weak">
                    ({numberOfReviews} {t('reviews')})
                  </TextLinkButton>
                </Text>
              </Inline>
            ) : null}
          </Box>
        </Box>
      </Column>
      <Column width="1/3">
        <Box display="flex" justifyContent="center">
          <ButtonLink
            onClick={() => {
              void handleWriteAReviewClicked();
            }}
            href={buildWriteAReviewURI({
              country,
              language,
              environment,
              consolidated,
              params: model?.name
                ? `cn=${encodeURIComponent(model?.name)}&tracking=company-profile`
                : undefined,
            })}
            variant="solid"
            rel="nofollow"
            size="standard"
            tone="formAccent"
            icon={<IconEdit />}
          >
            {t('Write a review')}
          </ButtonLink>
        </Box>
      </Column>
    </Columns>
  );
}
